import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/private-docs`;
let cached = { token: null, at: 0 };
const fresh = () => (cached.token && Date.now() - cached.at < 4.5 * 60 * 1000 ? cached.token : null);

// Saves a generated file into the owner's private vault (asks PIN when needed).
export const SaveToPrivateDialog = ({ open, onOpenChange, defaultName, makeBlob, fileName }) => {
  const [pin, setPin] = useState("");
  const [token, setToken] = useState(null);
  const [folders, setFolders] = useState([]);
  const [f, setF] = useState({ name: "", folder_id: "", expiry_date: "" });
  const [busy, setBusy] = useState(false);
  const hdr = (t) => ({ headers: { "X-Private-Token": t } });
  const loadFolders = (t) => axios.get(API, hdr(t)).then((r) => { setFolders(r.data.folders); cached = { token: t, at: Date.now() }; setToken(t); }).catch(() => setToken(null));
  useEffect(() => { if (!open) return; setF({ name: defaultName || "", folder_id: "", expiry_date: "" }); setPin(""); const t = fresh(); if (t) loadFolders(t); else setToken(null); }, [open, defaultName]);
  const unlock = async () => {
    setBusy(true);
    try { const t = (await axios.post(`${API}/unlock`, { pin })).data.token; await loadFolders(t); }
    catch (e) { toast.error(e?.response?.data?.detail || "الرمز غير صحيح"); setPin(""); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const blob = await makeBlob();
      const fd = new FormData(); fd.append("file", blob, fileName); Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/docs`, fd, hdr(token)); cached.at = Date.now();
      toast.success("تم الحفظ في الوثائق الخاصة 🔒"); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); if (e?.response?.status === 401) setToken(null); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-sm" data-testid="save-private-dialog">
        <DialogHeader className="text-right"><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> حفظ في الوثائق الخاصة</DialogTitle>
          <DialogDescription>{token ? "اختر المجلد واسم الوثيقة" : "أدخل رمز الوثائق الخاصة"}</DialogDescription></DialogHeader>
        {!token ? (
          <div className="space-y-3">
            <Input data-testid="save-private-pin" type="password" inputMode="numeric" maxLength={6} autoFocus value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && pin.length >= 4 && unlock()} className="h-12 text-center text-2xl tracking-[0.5em]" dir="ltr" />
            <Button data-testid="save-private-unlock" disabled={busy || pin.length < 4} onClick={unlock} className="w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "فتح"}</Button>
            <p className="text-center text-[11px] text-slate-400">لم تضع رمزاً بعد؟ افتح «وثائق خاصة» من الشؤون الإدارية أولاً</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Input data-testid="save-private-name" value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} placeholder="اسم الوثيقة" />
            <select data-testid="save-private-folder" value={f.folder_id} onChange={(e) => setF((p) => ({ ...p, folder_id: e.target.value }))} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">بدون مجلد</option>{folders.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <label className="block space-y-1 text-xs text-slate-500">تاريخ انتهاء (اختياري)<Input type="date" value={f.expiry_date} onChange={(e) => setF((p) => ({ ...p, expiry_date: e.target.value }))} /></label>
            <Button data-testid="save-private-submit" disabled={busy} onClick={save} className="w-full gap-1.5 bg-slate-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} حفظ</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
